"use client"

import type React from "react"

import { useCallback, useMemo } from "react"
import { isOptionDisabled, getSyncTypeLabel, getCardClassName, getAriaLabel } from "../utils/sync-option-utils"
import type { SyncOption } from "../types/choose-sync"

interface UseSyncOptionCardProps {
  option: SyncOption
  isSelected: boolean
  onSelect: (optionId: string) => void
}

export const useSyncOptionCard = ({ option, isSelected, onSelect }: UseSyncOptionCardProps) => {
  // Memoize computed values
  const isDisabled = useMemo(
    () => option.isDisabled ?? isOptionDisabled(option.source_object),
    [option.isDisabled, option.source_object],
  )

  const syncTypeLabel = useMemo(() => getSyncTypeLabel(option.supported_directions), [option.supported_directions])

  const cardClassName = useMemo(() => getCardClassName(isSelected, isDisabled), [isSelected, isDisabled])

  const ariaLabel = useMemo(() => getAriaLabel(option.name, isDisabled), [option.name, isDisabled])

  // Memoized event handlers
  const handleClick = useCallback(() => {
    if (!isDisabled) {  
      onSelect(option.id);
      localStorage.setItem("source_object",option.source_object);
      localStorage.setItem("target_object",option.target_object);
    }
  }, [isDisabled, onSelect, option.id])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled) return

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        onSelect(option.id)
      }
    },
    [isDisabled, onSelect, option.id],
  )

  const handleRadioChange = useCallback(() => {
    if (!isDisabled) {
      onSelect(option.id)
    }
  }, [isDisabled, onSelect, option.id])

  return {
    isDisabled,
    syncTypeLabel,
    cardClassName,
    ariaLabel,
    handleClick,
    handleKeyDown,
    handleRadioChange,
  }
}
